import { Box, Typography } from "@mui/material";
import React from "react";
import IconImagePreview from "../../../../pages/components/IconImagePreview";
import IOSChevronButton from "./IOSChevronButton";
import { useSlidingAppearance } from "../lib/AppearanceContext";
import { getSlidingSafeAreaTop } from "../styles/mainStyles";

const systemFont =
    '-apple-system, BlinkMacSystemFont, "SF Pro Text", "Segoe UI", sans-serif';

/**
 * Play screen top bar: back, centered title, optional view original (Screen 4).
 *
 * `compact` drops the row's own spacing and left-aligns the title so the caller can fold it into
 * another row — landscape has too little height for a separate title bar above the toolbar.
 */
export default function PlayPageNav({
    title,
    onBack,
    originalImageUrl = null,
    previewBoardSize = null,
    overlayBoardRect = null,
    getOverlayBoardRect = null,
    backAriaLabel = "Back",
    viewOriginalAriaLabel = "View original image",
    trailing = null,
    compact = false,
    /** Landscape folds the controls into a side rail, where a title has nowhere to go. */
    showTitle = true,
}) {
    const { tokens } = useSlidingAppearance();
    const hasTrailing = Boolean(trailing || originalImageUrl);
    const safeTop = getSlidingSafeAreaTop();

    const iconButtonSx = {
        color: tokens.text,
        minWidth: 44,
        minHeight: 44,
        width: 44,
        height: 44,
    };

    const titleSx = {
        fontFamily: systemFont,
        fontWeight: 600,
        fontSize: "1rem",
        textAlign: compact ? "left" : "center",
        minWidth: 0,
        px: 0.5,
        lineHeight: 1.35,
        color: tokens.text,
        ...(compact
            ? null
            : {
                  fontWeight: 700,
                  fontSize: { xs: "1.125rem", sm: "1.25rem" },
                  textAlign: "left",
                  flex: 1,
                  pr: 0.5,
                  lineHeight: 1.3,
                  "@media (min-width: 1200px) and (orientation: landscape)": {
                      fontSize: "clamp(1.5rem, 2.2vw, 2rem)",
                      fontWeight: 700,
                      letterSpacing: "-0.01em",
                      lineHeight: 1,
                      display: "flex",
                      alignItems: "center",
                      minHeight: 56,
                  },
              }),
    };

    if (compact) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    flex: showTitle ? "1 1 auto" : "0 0 auto",
                    minWidth: 0,
                    gap: 0.25,
                }}
            >
                <IOSChevronButton
                    direction="left"
                    ariaLabel={backAriaLabel}
                    onClick={onBack}
                />
                {showTitle ? (
                    <Typography variant="subtitle1" component="h1" noWrap sx={titleSx}>
                        {title}
                    </Typography>
                ) : null}
            </Box>
        );
    }

    return (
        <Box
            component="header"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                pt: `calc(${safeTop} + 0.5rem)`,
                mb: 1,
                flexShrink: 0,
                minWidth: 0,
                "@media (min-width: 1200px) and (orientation: landscape)": {
                    pt: 0,
                    mb: 0,
                    minHeight: 56,
                    alignItems: "center",
                },
            }}
        >
            <IOSChevronButton direction="left" ariaLabel={backAriaLabel} onClick={onBack} />

            <Typography
                variant="subtitle1"
                component="h1"
                noWrap
                sx={titleSx}
            >
                {title}
            </Typography>

            {hasTrailing ? (
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        alignItems: "center",
                        gap: 0,
                        minHeight: 44,
                        flexShrink: 0,
                    }}
                >
                    {trailing}
                    {originalImageUrl ? (
                        <IconImagePreview
                            imageUrl={originalImageUrl}
                            tooltip={viewOriginalAriaLabel}
                            hideTitle
                            centeredOverlay
                            overlayBoardSize={previewBoardSize}
                            overlayBoardRect={overlayBoardRect}
                            getOverlayBoardRect={getOverlayBoardRect}
                            sx={iconButtonSx}
                        />
                    ) : null}
                </Box>
            ) : null}
        </Box>
    );
}
